import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Building, IndianRupee, DollarSign, TrendingUp, UserCheck, Users } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency as marketFormatCurrency } from '@/lib/market';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RING_CIRCUMFERENCE = 201.06; // 2 * pi * 32 (r=32 ring)

const RMDashboard = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [families, setFamilies] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredKpi, setHoveredKpi] = useState(null);
  const navigate = useNavigate();

  const RISK_DESCRIPTIONS = {
    Conservative: 'Conservative: prioritizes capital preservation, lower tolerance for volatility',
    Balanced: 'Balanced: moderate growth with a balanced risk and return profile',
    Aggressive: 'Aggressive: growth focused, higher tolerance for volatility',
    'Not Assessed': 'Risk profile not yet assessed for this client',
  };


  useEffect(() => {
    fetchRMData();
  }, []);

  const fetchRMData = async () => {
    try {
      const [usersRes, statsRes, familiesRes, txnRes] = await Promise.all([
        axios.get(`${API}/rm/users`),
        axios.get(`${API}/rm/stats`),
        axios.get(`${API}/families`),
        axios.get(`${API}/transactions/recent`).catch(() => ({ data: [] }))
      ]);

      const allUsers = usersRes.data;
      setUsers(allUsers.filter(u => u.user_type === 'client'));
      setStats(statsRes.data);

      if (familiesRes.data && familiesRes.data.families) {
        setFamilies(familiesRes.data.families);
      }

      if (Array.isArray(txnRes.data)) {
        setRecentActivity(txnRes.data);
      }
    } catch (error) {
      console.error('Error fetching RM data:', error);
      if (error.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => marketFormatCurrency(value, user?.market);
  const formatCr = (value) => (value / 1e7).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Asset Allocation Chart (aggregated) - mock values, matches prior dashboard behavior
  const getAssetAllocationChart = () => ({
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c}%',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: 'rgba(255, 216, 122, 0.3)',
      textStyle: { color: '#1f2937' }
    },
    legend: {
      orient: 'horizontal',
      bottom: '5%',
      left: 'center',
      textStyle: { color: '#6b7280', fontSize: 11 },
      itemGap: 10,
      itemWidth: 12,
      itemHeight: 12
    },
    series: [{
      type: 'pie',
      radius: ['40%', '65%'],
      center: ['50%', '45%'],
      avoidLabelOverlap: true,
      label: { show: false },
      emphasis: { label: { show: true, fontSize: 14, fontWeight: 'bold', color: '#1f2937' } },
      labelLine: { show: false },
      data: [
        { value: 56.09, name: 'Equity', itemStyle: { color: '#FED879' } },
        { value: 31.12, name: 'Debt', itemStyle: { color: '#CCD5AE' } },
        { value: 8.74, name: 'International', itemStyle: { color: '#CEDEAB' } },
        { value: 2.43, name: 'Alternates', itemStyle: { color: '#DEE4DA' } },
        { value: 1.63, name: 'Liquid', itemStyle: { color: '#CFCECE' } }
      ]
    }]
  });

  // Return Summary Chart - mock values, matches prior dashboard behavior
  const getReturnSummaryChart = () => ({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: 'rgba(255, 216, 122, 0.3)',
      textStyle: { color: '#1f2937' }
    },
    legend: {
      data: ['Portfolio', 'Benchmark'],
      textStyle: { color: '#6b7280', fontSize: 11 },
      top: '5%',
      left: 'center',
      itemWidth: 12,
      itemHeight: 12,
      itemGap: 15
    },
    grid: { left: '8%', right: '8%', bottom: '12%', top: '20%', containLabel: true },
    xAxis: {
      type: 'category',
      data: ['MTD', 'YTD', '1Y', 'SI'],
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { color: '#6b7280', fontSize: 11 }
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { color: '#6b7280', formatter: '{value}%', fontSize: 11 },
      splitLine: { lineStyle: { color: '#f3f4f6' } }
    },
    series: [
      { name: 'Portfolio', type: 'bar', data: [0.95, 27.77, 37.31, 21.46], itemStyle: { color: '#FED879' }, barWidth: '35%' },
      { name: 'Benchmark', type: 'bar', data: [0.22, 17.84, 24.29, 13.84], itemStyle: { color: '#CCD5AE' }, barWidth: '35%' }
    ]
  });

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  const familyAumTotal = families.reduce((sum, f) => sum + (f.total_aum || 0), 0);
  const clientAumTotal = stats?.total_aum || 0;
  const totalAum = clientAumTotal + familyAumTotal;
  const avgReturn = users.length > 0
    ? users.reduce((sum, u) => {
        const net = u.net_capital || 0;
        const gain = u.portfolio_gain || 0;
        return sum + (net > 0 ? (gain / net) * 100 : 0);
      }, 0) / users.length
    : 0;

  // Build client cards: an aggregate "All Clients" card first, then one per real client.
  const buildRing = (pct) => {
    const clamped = Math.max(0, Math.min(100, pct));
    const offset = (RING_CIRCUMFERENCE - (clamped / 100) * RING_CIRCUMFERENCE).toFixed(2);
    return offset;
  };

  const aggregateCustodianMap = {};
  users.forEach(u => (u.custodians || []).forEach(cu => {
    aggregateCustodianMap[cu.name] = (aggregateCustodianMap[cu.name] || 0) + cu.value;
  }));
  const aggregateCustodians = Object.entries(aggregateCustodianMap).map(([name, value]) => ({ name, value }));

  const aggregateNet = users.reduce((s, u) => s + (u.net_capital || 0), 0);
  const aggregateGain = users.reduce((s, u) => s + (u.portfolio_gain || 0), 0);
  const aggregatePct = aggregateNet > 0 ? Math.round((aggregateGain / aggregateNet) * 100) : 0;

  const clientCards = [
    {
      isAggregate: true,
      name: 'All Clients',
      riskLabel: 'Balanced',
      currentValue: clientAumTotal,
      netCapital: aggregateNet,
      gainLoss: aggregateGain,
      returnPct: aggregatePct,
      custodians: aggregateCustodians,
    },
    ...users.map(u => {
      const net = u.net_capital || 0;
      const gain = u.portfolio_gain || 0;
      const pct = net > 0 ? Math.round((gain / net) * 100) : 0;
      return {
        isAggregate: false,
        name: u.full_name,
        riskLabel: u.risk_profile || 'Not Assessed',
        currentValue: u.portfolio_value || 0,
        netCapital: net,
        gainLoss: gain,
        returnPct: pct,
        custodians: u.custodians || [],
      };
    }),
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-7">
        {/* Header */}
        <div className="flex items-baseline justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'PerfectNineties', serif" }}>
              Welcome, {user?.full_name}
            </h1>
            <p className="text-sm text-gray-600">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              {' · '}Investment Banking &amp; Wealth Desk
            </p>
          </div>
          <div className="text-right">
            <p className="text-[11px] tracking-wide uppercase text-gray-400 mb-0.5">Book Snapshot</p>
            <p className="text-[22px] font-semibold text-gray-900">{formatCurrency(totalAum)} AUM</p>
          </div>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[
            {
              icon: <UserCheck className="text-[#1a1a1a]" size={20} />,
              value: users.length,
              label: 'Total Clients',
              tooltip: 'Total number of clients currently assigned to you',
              highlight: false,
            },
            {
              icon: <Users className="text-[#1a1a1a]" size={20} />,
              value: formatCurrency(clientAumTotal),
              label: 'Client AUM',
              tooltip: 'Combined current portfolio value across all your individual clients',
              highlight: false,
            },
            {
              icon: <Building className="text-[#1a1a1a]" size={20} />,
              value: formatCurrency(familyAumTotal),
              label: 'Family AUM',
              tooltip: 'Combined AUM across all household/family groups you manage',
              highlight: false,
            },
            {
              icon: user?.market === 'US' ? <DollarSign className="text-[#1a1a1a]" size={20} /> : <IndianRupee className="text-[#1a1a1a]" size={20} />,
              value: formatCurrency(totalAum),
              label: 'Total AUM',
              tooltip: 'Client AUM plus Family AUM combined, your full book value',
              highlight: true,
            },
            {
              icon: <TrendingUp className="text-[#1a1a1a]" size={20} />,
              value: `+${avgReturn.toFixed(1)}%`,
              label: 'Average Returns',
              tooltip: 'Average unrealized return (gain / net capital) across your clients',
              highlight: false,
              valueClass: 'text-green-600',
            },
          ].map((kpi, i) => {
            const isHovered = hoveredKpi === i;
            return (
              <div
                key={i}
                title={kpi.tooltip}
                onMouseEnter={() => setHoveredKpi(i)}
                onMouseLeave={() => setHoveredKpi(null)}
                className={`rounded-xl p-4 flex items-center gap-3.5 transition-all duration-200 ${
                  kpi.highlight ? 'bg-[#fdf9ec] border border-[#ffd87a]' : 'bg-white border border-ugmonk-border shadow-sm'
                }`}
                style={{
                  transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
                  boxShadow: isHovered ? '0 6px 16px rgba(0,0,0,0.08)' : undefined,
                }}
              >
                <div
                  className="w-[42px] h-[42px] rounded-[11px] flex items-center justify-center flex-shrink-0"
                  style={{ background: kpi.highlight ? 'rgba(255,216,122,0.35)' : '#fffdf0' }}
                >
                  {kpi.icon}
                </div>
                <div className="min-w-0 flex flex-col gap-0.5">
                  <p className={`text-xl font-semibold leading-none whitespace-nowrap ${kpi.valueClass || 'text-gray-900'}`}>{kpi.value}</p>
                  <p className={kpi.highlight ? 'text-[11.5px] text-[#404040] font-medium uppercase tracking-wide' : 'text-xs text-gray-500'}>{kpi.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Client Portfolios (PRIMARY) */}
        <div>
          <div className="flex items-center justify-between mb-3.5">
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-0.5">Client Portfolios</h2>
              <p className="text-xs text-gray-400">Top clients by current portfolio value</p>
            </div>
            <button onClick={() => navigate('/clients-prospects')} className="text-sm font-medium text-ugmonk-dark hover:text-[#1a1a1a]">
              View All Clients →
            </button>
          </div>

          {users.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-500">
              No clients assigned yet
            </div>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
              {clientCards.map((c, idx) => {
                const isHovered = hoveredCard === idx;
                return (
                <div
                  key={idx}
                  role="button"
                  tabIndex={0}
                  title={c.isAggregate ? 'Aggregate view across all your clients. Click to open Clients & Prospects' : `${c.name}. Click to view full client details`}
                  onMouseEnter={() => setHoveredCard(idx)}
                  onMouseLeave={() => setHoveredCard(null)}
                  onClick={() => navigate('/clients-prospects')}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/clients-prospects'); }}
                  className="bg-white rounded-2xl p-5 cursor-pointer transition-all duration-200"
                  style={{
                    flex: '1 1 280px',
                    minWidth: '280px',
                    border: c.isAggregate ? '1px solid #ffd87a' : (isHovered ? '1px solid #ccd5ae' : '1px solid #e5e5e5'),
                    boxShadow: isHovered
                      ? '0 8px 20px rgba(0,0,0,0.08)'
                      : (c.isAggregate ? '0 2px 10px rgba(255,216,122,0.18)' : 'none'),
                    transform: isHovered ? 'translateY(-3px)' : 'translateY(0)',
                  }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-[15px] font-semibold text-gray-900 mb-1">{c.name}</h3>
                      <span
                        title={RISK_DESCRIPTIONS[c.riskLabel] || `Risk profile: ${c.riskLabel}`}
                        className="text-[10.5px] px-2.5 py-0.5 rounded-full font-medium"
                        style={{ background: 'rgba(204,213,174,0.35)', color: '#5c6647' }}
                      >
                        {c.riskLabel}
                      </span>
                    </div>
                    <div className="relative w-14 h-14" title={`Return ${c.returnPct}% (Gain ${formatCurrency(c.gainLoss)} on Net Capital ${formatCurrency(c.netCapital)})`}>
                      <svg width="56" height="56" viewBox="0 0 80 80">
                        <circle cx="40" cy="40" r="32" fill="none" stroke="#f1f1e9" strokeWidth="7" />
                        <circle
                          cx="40" cy="40" r="32" fill="none"
                          stroke={c.isAggregate ? '#FED879' : '#CCD5AE'}
                          strokeWidth="7" strokeLinecap="round"
                          strokeDasharray={RING_CIRCUMFERENCE}
                          strokeDashoffset={buildRing(c.returnPct)}
                          transform="rotate(-90 40 40)"
                          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-gray-900">{c.returnPct}%</div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 pb-3.5 mb-3.5 border-b border-[#f0f0ea]">
                    <div className="flex items-baseline justify-between gap-2" title="Current market value of this portfolio">
                      <span className="text-xs text-gray-400 whitespace-nowrap">Current Value</span>
                      <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(c.currentValue)}</span>
                    </div>
                    <div className="flex items-baseline justify-between gap-2" title="Total capital invested (cost basis)">
                      <span className="text-xs text-gray-400 whitespace-nowrap">Net Capital</span>
                      <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(c.netCapital)}</span>
                    </div>
                    <div className="flex items-baseline justify-between gap-2" title="Unrealized gain or loss since investment">
                      <span className="text-xs text-gray-400 whitespace-nowrap">Gain / Loss</span>
                      <span className="text-sm font-semibold whitespace-nowrap" style={{ color: '#5c7a3a' }}>{formatCurrency(c.gainLoss)}</span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    {c.custodians.length === 0 ? (
                      <p className="text-[11px] text-gray-400">No custodian data available</p>
                    ) : (
                      c.custodians.map((cu, i) => (
                        <div
                          key={i}
                          title={`${cu.name}: ${formatCurrency(cu.value)} held with this custodian`}
                          className="flex items-center justify-between bg-[#f6f7f0] rounded-lg px-2.5 py-2 transition-colors hover:bg-[#eef0e4]"
                        >
                          <span className="text-[11.5px] text-gray-600">{cu.name}</span>
                          <span className="text-xs font-semibold text-gray-900">{formatCurrency(cu.value)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );})}
            </div>
          )}
        </div>

        {/* Bottom Analytics Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-gray-200">
            <h3 className="text-gray-900 font-semibold mb-4">Asset Allocation</h3>
            <div className="relative" style={{ minHeight: '280px' }}>
              <ReactECharts option={getAssetAllocationChart()} style={{ height: '280px', width: '100%' }} opts={{ renderer: 'svg' }} />
              <div className="absolute" style={{ top: '42%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
                <p className="text-xl font-bold text-gray-900 text-center whitespace-nowrap">{formatCurrency(totalAum)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-gray-200">
            <h3 className="text-gray-900 font-semibold mb-4">Return Summary</h3>
            <div style={{ minHeight: '280px' }}>
              <ReactECharts option={getReturnSummaryChart()} style={{ height: '280px', width: '100%' }} opts={{ renderer: 'svg' }} />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-gray-200">
            <h3 className="text-gray-900 font-semibold mb-3.5">Recent Transactions</h3>
            <div className="flex flex-col">
              {recentActivity.length === 0 ? (
                <p className="text-sm text-gray-400 py-4 text-center">No recent transactions</p>
              ) : (
                recentActivity.slice(0, 5).map((txn, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2.5 border-b border-[#f2f2ee] last:border-0">
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-gray-900 mb-0.5 whitespace-nowrap overflow-hidden text-ellipsis" style={{ maxWidth: '230px' }}>{txn.fund}</p>
                      <p className="text-[11px] text-gray-400">{txn.investor || txn.client} · {txn.date}</p>
                    </div>
                    <div className="text-right flex-shrink-0 pl-2.5">
                      <p className="text-[13px] font-semibold text-gray-900 mb-0.5">{txn.value || txn.amount}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        txn.type === 'Buy' ? 'bg-green-100 text-green-700' :
                        txn.type === 'Dividend' ? 'bg-[#fef3d6] text-[#7a5c00]' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {txn.type}
                      </span>
                    </div>
                  </div>
                ))
              )}
              <button onClick={() => navigate('/clients-prospects')} className="text-xs font-medium text-center pt-3 text-ugmonk-dark hover:text-[#1a1a1a]">
                View All Transactions →
              </button>
            </div>
          </div>
        </div>

        {/* Family Snapshot (SECONDARY) */}
        <div className="bg-white rounded-2xl p-5 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-gray-900 font-semibold mb-0.5">Family Snapshot</h3>
              <p className="text-xs text-gray-400">Household-level view across managed families</p>
            </div>
            <button
              onClick={() => navigate('/clients-prospects', { state: { tab: 'family-management' } })}
              className="text-sm font-medium text-ugmonk-dark hover:text-[#1a1a1a]"
            >
              View All Families →
            </button>
          </div>
          {families.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No families found</p>
            </div>
          ) : (
            <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
              {families.slice(0, 6).map((family) => (
                <div
                  key={family.family_id}
                  title={`${family.family_name}, ${family.total_owners || 0} owners. Click to view family details`}
                  className="border border-[#eeeee6] rounded-xl p-4 flex flex-col gap-3 cursor-pointer hover:border-[#ffd87a] hover:shadow-md transition-all duration-200"
                  onClick={() => navigate('/clients-prospects', { state: { tab: 'family-management' } })}
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center text-sm font-semibold flex-shrink-0" style={{ background: 'rgba(204,213,174,0.35)', color: '#5c6647' }}>
                      {family.family_name?.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13.5px] font-semibold text-gray-900 mb-0.5 whitespace-nowrap overflow-hidden text-ellipsis">{family.family_name}</p>
                      <p className="text-[11px] text-gray-400">Head: {family.family_head}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2.5 border-t border-[#f2f2ee]">
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Family AUM</p>
                      <p className="text-[15px] font-semibold text-gray-900">{formatCurrency(family.total_aum || 0)}</p>
                    </div>
                    <span className="text-[11px] px-2.5 py-1 rounded-full font-medium bg-[#f6f7f0] text-gray-600">
                      {family.total_owners || 0} owners
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default RMDashboard;
